import ConvertedCurrency from "@/pages/deals/components/commercials/ConvertedCurrency";
import { formatCurrency, totalBeforeDiscount } from "../consts";
import Text from "@/components/ui/text";
import { ContractRevenueItem } from "@/pages/contracts/types";

interface TotalCellProps {
  // A line, or the sum of a bundle's lines.
  item: Pick<
    ContractRevenueItem,
    "total" | "totalLocal" | "discountAmount" | "isDiscountPercentage"
  >;
  currency: string;
  showConversion: boolean;
  rate: number | undefined;
  accountCurrencySymbol: string;
  // The sign formatCurrency should print, already resolved against the account's beside it.
  currencySignOverride?: string;
  // Both currencies' totals before the discount, when known rather than
  // recovered from the total: a bundle's, whose amount discount is in one
  // currency only.
  totalsBeforeDiscount?: Pick<ContractRevenueItem, "total" | "totalLocal">;
}

// A line with no total is unknown, not zero; formatCurrency renders null as an em dash.
const shownTotal = (
  totals: Pick<ContractRevenueItem, "total" | "totalLocal">,
  showConversion: boolean
) =>
  showConversion
    ? (totals.totalLocal ?? totals.total ?? null)
    : (totals.total ?? totals.totalLocal ?? null);

const TotalCell: React.FC<TotalCellProps> = ({
  item,
  currency,
  showConversion,
  rate,
  accountCurrencySymbol,
  currencySignOverride,
  totalsBeforeDiscount
}) => {
  const total = shownTotal(item, showConversion);
  const discountAmount = item.discountAmount ?? 0;
  // Only a real discount has a figure to strike through; recovering it is the
  // same inversion the drawer uses, so a whole-total discount hides rather than
  // rendering an infinity and a downsell shows its own negative original.
  const originalTotal =
    discountAmount <= 0
      ? null
      : totalsBeforeDiscount
        ? shownTotal(totalsBeforeDiscount, showConversion)
        : totalBeforeDiscount(total, discountAmount, item.isDiscountPercentage);

  return (
    <div className="flex flex-col items-start gap-0.5">
      {originalTotal != null && originalTotal !== total && (
        <div className="relative flex items-center gap-1 text-tertiary after:absolute after:inset-x-0 after:top-1/2 after:h-px after:bg-current after:content-['']">
          <Text variant="smBodySecondary">
            {formatCurrency(
              originalTotal,
              currency,
              undefined,
              currencySignOverride
            )}
          </Text>
          {showConversion && originalTotal > 0 && (
            <ConvertedCurrency
              amount={originalTotal}
              rate={rate}
              accountCurrencySymbol={accountCurrencySymbol}
              variant="smBodyTertiary"
            />
          )}
        </div>
      )}
      <div className="flex items-center gap-1">
        <Text variant="body" className="font-medium">
          {formatCurrency(total, currency, undefined, currencySignOverride)}
        </Text>
        {showConversion && total != null && total > 0 && (
          <ConvertedCurrency
            amount={total}
            rate={rate}
            accountCurrencySymbol={accountCurrencySymbol}
            variant="bodyTertiary"
          />
        )}
      </div>
    </div>
  );
};

export default TotalCell;
